import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';

import {
  PointerDownCommand,
  PointerMoveCommand,
  PointerUpCommand,
} from '@canvas/databases-bridge-api/commands';

import { getElementFromEventTarget, isInteractiveElement } from './utils';

type ForwardPointerEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

export function setupPointerEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardPointerEventsParams): () => void {
  let isForwarding = false;

  const handlePointerDown = (event: PointerEvent) => {
    const target = getElementFromEventTarget(event.target);
    if (!target) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    isForwarding = true;

    bridge.send(PointerDownCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(PointerMoveCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (!isForwarding) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(PointerUpCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createEventProperties(event, isInteractiveDbElement),
    });

    isForwarding = false;
  };

  const eventOptions = {
    passive: false,
    capture: true,
  };

  document.addEventListener('pointerdown', handlePointerDown, eventOptions);
  document.addEventListener('pointermove', handlePointerMove, eventOptions);
  document.addEventListener('pointerup', handlePointerUp, eventOptions);

  return () => {
    document.removeEventListener('pointerdown', handlePointerDown, eventOptions);
    document.removeEventListener('pointermove', handlePointerMove, eventOptions);
    document.removeEventListener('pointerup', handlePointerUp, eventOptions);
  };
}

const createEventProperties = (event: PointerEvent, isInteractiveDbElement = false) => ({
  clientX: event.clientX,
  clientY: event.clientY,
  button: event.button,
  buttons: event.buttons,
  pointerId: event.pointerId,
  pointerType: event.pointerType,
  viewportWidth: document.documentElement.clientWidth,
  viewportHeight: document.documentElement.clientHeight,
  ctrlKey: event.ctrlKey,
  shiftKey: event.shiftKey,
  altKey: event.altKey,
  metaKey: event.metaKey,
  isInteractiveDbElement,
});
